import { useQuery } from "@tanstack/react-query";
import { berserkr, isSupabaseConfigured } from "../lib/supabase";
import type { FilingParagraphRow } from "./useCaseFilings";

export interface ParagraphHit extends FilingParagraphRow {
  /** Joined-in: the filing this paragraph belongs to. */
  filing: {
    id: string;
    title: string;
    filing_kind: string;
    filed_at: string | null;
    case_number: string;
  };
  /** Joined-in: the section this paragraph belongs to. */
  section: {
    id: string;
    section_kind: string;
    ordinal: number | null;
    title: string | null;
  };
}

/**
 * The Retriever's bread and butter. Given a case + a free-text query,
 * find paragraphs whose body matches and return them with citations
 * (filing + section context).
 *
 * v0 search: ILIKE on `body`. Works for small corpora. Switch to
 * tsvector/tsquery (full-text) or vector similarity once corpus grows
 * past a few hundred paragraphs.
 *
 * `enabled` is false when query is empty so we don't fire on every
 * keystroke before the user submits.
 */
export function useFilingSearch(
  caseId: string | undefined,
  query: string,
  options: { enabled?: boolean } = {},
) {
  const enabled =
    (options.enabled ?? true) &&
    isSupabaseConfigured() &&
    !!caseId &&
    query.trim().length >= 2;

  return useQuery<ParagraphHit[], Error>({
    queryKey: ["filing-search", caseId, query.trim()],
    enabled,
    queryFn: async () => {
      if (!caseId) return [];
      const trimmed = query.trim();
      if (trimmed.length < 2) return [];

      // 1. Find this case's filings via case_number.
      const { data: caseRow, error: caseErr } = await berserkr()
        .from("cases")
        .select("case_number")
        .eq("id", caseId)
        .maybeSingle();
      if (caseErr) throw new Error(caseErr.message);
      if (!caseRow) return [];

      const { data: filings, error: fErr } = await berserkr()
        .from("filings")
        .select(
          "id, title, filing_kind, filed_at, case_number",
        )
        .eq("case_number", (caseRow as { case_number: string }).case_number);
      if (fErr) throw new Error(fErr.message);
      if (!filings || filings.length === 0) return [];

      const filingsById = new Map(
        filings.map((f) => [(f as { id: string }).id, f]),
      );
      const filingIds = Array.from(filingsById.keys());

      // 2. Search paragraphs by body ILIKE.
      const { data: paragraphs, error: pErr } = await berserkr()
        .from("filing_paragraphs")
        .select(
          "id, filing_id, section_id, paragraph_number, body, target",
        )
        .in("filing_id", filingIds)
        .ilike("body", `%${trimmed}%`)
        .order("paragraph_number", { ascending: true })
        .limit(50);
      if (pErr) throw new Error(pErr.message);
      if (!paragraphs || paragraphs.length === 0) return [];

      // 3. Pull section metadata for the matched paragraphs.
      const sectionIds = Array.from(
        new Set(
          paragraphs.map((p: { section_id: string }) => p.section_id),
        ),
      );
      const { data: sections, error: sErr } = await berserkr()
        .from("filing_sections")
        .select("id, section_kind, ordinal, title")
        .in("id", sectionIds);
      if (sErr) throw new Error(sErr.message);

      const sectionsById = new Map(
        (sections ?? []).map((s) => [(s as { id: string }).id, s]),
      );

      return paragraphs.map((p) => {
        const pp = p as FilingParagraphRow;
        return {
          ...pp,
          filing: filingsById.get(pp.filing_id) as ParagraphHit["filing"],
          section: (sectionsById.get(pp.section_id) ?? {
            id: pp.section_id,
            section_kind: "unknown",
            ordinal: null,
            title: null,
          }) as ParagraphHit["section"],
        };
      });
    },
  });
}
